import { useState, useMemo, useCallback } from 'react';
import { AINutritionService } from '@/services/ai-nutrition.service';
import { MetabolicService } from '@/services/metabolic.service';

interface UseNutritionComposerProps {
    payload: any;
    updatePayload: (updates: any) => void;
    updatePreviewData: (data: any) => void;
}

/**
 * useNutritionComposer: Logic for the Metabolic Ledger.
 * HARDENED: Uses useCallback for stable handlers to prevent stale closures in memoized rows.
 * V6.4: Fixed deletion logic by stabilizing handler references.
 */
export const useNutritionComposer = ({ payload, updatePayload, updatePreviewData }: UseNutritionComposerProps) => {
    // 1. Data Normalization (Logic Layer)
    const items = useMemo(() => {
        const normalized = MetabolicService.normalizeLedgerItems(payload);
        return normalized;
    }, [payload]);

    const totals = useMemo(() => MetabolicService.calculateLedgerTotals(items), [items]);
    const tacticalTip = useMemo(() => MetabolicService.getTacticalNutritionTip(totals), [totals]);

    // 2. UI State
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [rawDescription, setRawDescription] = useState("");

    // 3. Handlers (Compute + IO Bridge)
    const syncState = useCallback((newItems: any[]) => {
        const newTotals = MetabolicService.calculateLedgerTotals(newItems);
        
        // Update payload with the rich totals object
        updatePayload({ 
            items: newItems, 
            ...newTotals 
        });
        
        updatePreviewData(newTotals);
    }, [updatePayload, updatePreviewData]);

    const handleAIAnalyze = useCallback(async () => {
        if (!rawDescription.trim()) return;
        setIsAnalyzing(true);
        try {
            const { data: results, error } = await AINutritionService.parseMealDescription(rawDescription);
            if (error) {
                alert("AI Decomposition failed: " + error);
                return;
            }
            if (results?.items) {
                const incoming = results.items.map((it: any) => ({ 
                    ...it, 
                    id: crypto.randomUUID(),
                    quantity: it.quantity || '---'
                }));
                syncState([...items, ...incoming]);
                setRawDescription("");
            }
        } catch (err) {
            console.error("[useNutritionComposer] AI Analyze Exception:", err);
        } finally {
            setIsAnalyzing(false);
        }
    }, [rawDescription, items, syncState]);

    const handleRemove = useCallback((id: string) => {
        syncState(items.filter((it: any) => it.id !== id));
    }, [items, syncState]);

    const handleUpdate = useCallback((id: string, field: string, value: any) => {
        const val = MetabolicService.sanitizeMacroValue(field, value);
        syncState(items.map((it: any) => it.id === id ? { ...it, [field]: val } : it));
    }, [items, syncState]);

    const handleUpdateItem = useCallback((id: string, updates: any) => {
        syncState(items.map((it: any) => it.id === id ? { ...it, ...updates } : it));
    }, [items, syncState]);

    const updateSequenceStrategy = useCallback((strategy: string) => {
        updatePayload({ sequence_strategy: strategy });
    }, [updatePayload]);

    const handleAddItem = useCallback(() => {
        const newItem = {
            id: crypto.randomUUID(),
            item_name: "New Item",
            calories: 0,
            protein: 0,
            carbs: 0,
            fat: 0,
            insulin_load: 0,
            glycemic_index: 0,
            insulin_spike_risk: "Low",
            quantity: "---"
        };
        syncState([...items, newItem]);
    }, [items, syncState]);

    return {
        items,
        totals,
        tacticalTip,
        isAnalyzing,
        rawDescription,
        setRawDescription,
        handleAIAnalyze,
        handleAddItem,
        handleRemove,
        handleUpdate,
        handleUpdateItem,
        updateSequenceStrategy
    };
};
